import { jsPDF } from 'jspdf';
import {
  ydocToJSON,
  isSceneHeadingParagraphText,
  isScriptParentheticalParagraphText,
  getParagraphExportLayouts,
  type ExportScriptParagraphRole,
} from '@narrativebench/editor-core';
import type { ExportLayoutOptions, ExportOptions } from './export-utils';
import {
  getExportBodyFontSizePt,
  getExportFilename,
  getJsPdfFontFamily,
  getPdfHeadingFontSizePt,
  resolveExportAppendixContent,
  resolveExportDocumentJson,
} from './export-utils';
import { ensurePdfExportFonts } from './export-pdf-fonts';
import {
  annotatePdfLinesWithMarkers,
  buildPdfCommentMarkers,
  buildPdfSuggestionMarkers,
} from './export-pdf-markers';
import {
  applyScriptRoleTextTransform,
  getMentionExportText,
  isScriptCharacterCueRole,
  isScriptDialogueRole,
  isScriptParentheticalRole,
  isScriptSceneHeadingRole,
  SCRIPT_DIALOGUE_INSET_RATIO,
  scriptCharacterCueTopGapMm,
  scriptSceneHeadingGapMm,
} from './export-script-format';
import {
  EXPORT_SCRIPT_STATS_FONT_SIZE_PT,
  EXPORT_SCRIPT_STATS_PDF_GREY_RGB,
  collectExportScriptStatsQueue,
  takeNextExportScriptStatsLine,
} from './export-script-stats';

const FIRST_LINE_INDENT_EM = 2;

const firstLineIndentMmFromBodyFontSizePt = (fontSizePt: number): number =>
  (fontSizePt * FIRST_LINE_INDENT_EM * 25.4) / 72;

const paragraphStartsWithCharacterMentionJson = (node: any): boolean => {
  if (!node.content || !Array.isArray(node.content)) {
    return false;
  }
  for (const child of node.content) {
    if (child.type === 'text') {
      if ((child.text || '').trim().length > 0) {
        return false;
      }
      continue;
    }
    if (child.type === 'scriptMention') {
      return child.attrs?.entityType === 'character';
    }
    return false;
  }
  return false;
};

const countParagraphsInJsonMain = (node: any): number => {
  if (node.type === 'paragraph') {
    return 1;
  }
  if (!node.content || !Array.isArray(node.content)) {
    return 0;
  }
  return node.content.reduce((sum: number, child: any) => {
    if (child.type === 'footnotes') {
      return sum;
    }
    return sum + countParagraphsInJsonMain(child);
  }, 0);
};

const wrapParagraphLinesWithOptionalFirstLineIndent = (
  pdf: jsPDF,
  text: string,
  maxWidth: number,
  firstLineIndentMm: number,
): string[] => {
  if (!text) {
    return [''];
  }
  if (firstLineIndentMm <= 0) {
    return pdf.splitTextToSize(text, maxWidth);
  }
  const firstWidth = Math.max(8, maxWidth - firstLineIndentMm);
  const firstChunk = pdf.splitTextToSize(text, firstWidth);
  const firstLine = firstChunk[0];
  let rest = '';
  if (text.startsWith(firstLine)) {
    rest = text.slice(firstLine.length).replace(/^\s+/, '');
  } else {
    const idx = text.indexOf(firstLine);
    if (idx >= 0) {
      rest = text.slice(idx + firstLine.length).replace(/^\s+/, '');
    } else {
      return pdf.splitTextToSize(text, maxWidth);
    }
  }
  const restLines = rest ? pdf.splitTextToSize(rest, maxWidth) : [];
  return [firstLine, ...restLines];
};

interface LineWithFootnotes {
  text: string;
  isHeading: boolean;
  level?: number;
  indentFirstLine?: boolean;
  scriptRole?: ExportScriptParagraphRole;
  isScriptStats?: boolean;
  runs?: TextRun[];
}

interface PdfTipTapContext {
  indentFlags: boolean[] | null;
  scriptRoles: ExportScriptParagraphRole[] | null;
  paragraphIndexRef: { current: number };
  inList: boolean;
  scriptStatsQueue: Array<string | null>;
}

interface FootnoteData {
  id: string;
  content: Array<{ text: string; isHeading: boolean; level?: number }>;
}

interface TextRun {
  text: string;
  isBold: boolean;
  isItalic: boolean;
}

interface StyledWord {
  text: string;
  isBold: boolean;
  isItalic: boolean;
}

const getFootnoteNumberFromReference = (
  child: any,
  footnoteMap: Map<string, number>,
): number | undefined => {
  const footnoteDataId = child.attrs?.['data-id'] || child.attrs?.dataId;
  const referenceNumber = child.attrs?.referenceNumber;
  if (footnoteDataId && footnoteMap.has(footnoteDataId)) {
    return footnoteMap.get(footnoteDataId);
  }
  if (referenceNumber) {
    const num = parseInt(referenceNumber, 10);
    if (!isNaN(num)) {
      return num;
    }
  }
  return undefined;
};

const appendRun = (runs: TextRun[], run: TextRun): void => {
  if (!run.text) {
    return;
  }
  const last = runs[runs.length - 1];
  if (last && last.isBold === run.isBold && last.isItalic === run.isItalic) {
    last.text += run.text;
    return;
  }
  runs.push(run);
};

const extractTextRuns = (content: any[] | undefined, footnoteMap: Map<string, number>): TextRun[] => {
  const runs: TextRun[] = [];
  if (!content || !Array.isArray(content)) {
    return runs;
  }
  content.forEach((child: any) => {
    if (child.type === 'text') {
      const marks = Array.isArray(child.marks) ? child.marks : [];
      appendRun(runs, {
        text: child.text || '',
        isBold: marks.some((mark: any) => mark?.type === 'bold'),
        isItalic: marks.some((mark: any) => mark?.type === 'italic'),
      });
      return;
    }
    if (child.type === 'hardBreak') {
      appendRun(runs, { text: '\n', isBold: false, isItalic: false });
      return;
    }
    if (child.type === 'mention' || child.type === 'scriptMention') {
      appendRun(runs, {
        text: getMentionExportText(child),
        isBold: false,
        isItalic: false,
      });
      return;
    }
    if (child.type === 'footnoteReference') {
      const footnoteNumber = getFootnoteNumberFromReference(child, footnoteMap);
      if (footnoteNumber) {
        appendRun(runs, { text: `[${footnoteNumber}]`, isBold: false, isItalic: false });
      }
    }
  });
  return runs;
};

const textFromRuns = (runs: TextRun[]): string => runs.map((run) => run.text).join('');

const splitRunsByLineBreak = (runs: TextRun[]): TextRun[][] => {
  const segments: TextRun[][] = [[]];
  runs.forEach((run) => {
    const parts = run.text.split('\n');
    parts.forEach((part, index) => {
      appendRun(segments[segments.length - 1], {
        text: part,
        isBold: run.isBold,
        isItalic: run.isItalic,
      });
      if (index < parts.length - 1) {
        segments.push([]);
      }
    });
  });
  return segments;
};

const getStyledWordsFromRuns = (runs: TextRun[]): StyledWord[] => {
  const words: StyledWord[] = [];
  runs.forEach((run) => {
    const found = run.text.match(/\S+/g) || [];
    found.forEach((word) => {
      words.push({
        text: word,
        isBold: run.isBold,
        isItalic: run.isItalic,
      });
    });
  });
  return words;
};

const getPdfFontStyle = (isBold: boolean, isItalic: boolean): 'normal' | 'bold' | 'italic' | 'bolditalic' => {
  if (isBold && isItalic) {
    return 'bolditalic';
  }
  if (isBold) {
    return 'bold';
  }
  if (isItalic) {
    return 'italic';
  }
  return 'normal';
};

const extractFootnotes = (node: any): { footnotes: FootnoteData[]; footnoteMap: Map<string, number> } => {
  const footnotes: FootnoteData[] = [];
  const footnoteMap = new Map<string, number>();
  let footnoteCounter = 1;

  if (node.type === 'doc' && node.content && Array.isArray(node.content)) {
    const footnotesNode = node.content.find((child: any) => child.type === 'footnotes');
    
    if (footnotesNode && footnotesNode.content) {
      footnotesNode.content.forEach((footnoteItem: any) => {
        if (footnoteItem.type === 'footnote') {
          const footnoteDataId = footnoteItem.attrs?.['data-id'] || footnoteItem.attrs?.dataId;
          const footnoteId = footnoteItem.attrs?.id || `fn:${footnoteCounter}`;
          const footnoteContent: Array<{ text: string; isHeading: boolean; level?: number }> = [];
          
          if (footnoteItem.content) {
            footnoteItem.content.forEach((fnChild: any) => {
              if (fnChild.type === 'paragraph' && fnChild.content) {
                const textParts: string[] = [];
                fnChild.content.forEach((textNode: any) => {
                  if (textNode.type === 'text') {
                    textParts.push(textNode.text || '');
                  } else if (textNode.type === 'hardBreak') {
                    textParts.push('\n');
                  }
                });
                const text = textParts.join('');
                if (text.trim() || footnoteContent.length === 0) {
                  footnoteContent.push({ text: text || '', isHeading: false });
                }
              }
            });
          }
          
          if (footnoteContent.length > 0) {
            const finalId = footnoteDataId || footnoteId;
            footnotes.push({
              id: finalId,
              content: footnoteContent,
            });
            
            if (footnoteDataId) {
              footnoteMap.set(footnoteDataId, footnoteCounter);
            }
            if (footnoteId) {
              footnoteMap.set(footnoteId, footnoteCounter);
            }
            footnoteCounter++;
          }
        }
      });
    }
  }

  return { footnotes, footnoteMap };
};

const convertTipTapToPDF = (
  node: any,
  lines: Array<LineWithFootnotes> = [],
  footnoteMap: Map<string, number> = new Map(),
  ctx: PdfTipTapContext,
): void => {
  if (node.type === 'doc') {
    if (node.content && Array.isArray(node.content)) {
      const mainContent = node.content.filter((child: any) => child.type !== 'footnotes');
      mainContent.forEach((child: any) => {
        convertTipTapToPDF(child, lines, footnoteMap, { ...ctx, inList: false });
      });
    }
  } else if (node.type === 'paragraph') {
    const paragraphIndex = ctx.paragraphIndexRef.current;
    const rawRuns = extractTextRuns(node.content, footnoteMap);
    const rawText = textFromRuns(rawRuns);
    let scriptRole: ExportScriptParagraphRole = ctx.scriptRoles?.[paragraphIndex] ?? 'body';
    if (!ctx.scriptRoles) {
      if (paragraphStartsWithCharacterMentionJson(node)) {
        scriptRole = 'character_cue';
      } else if (isSceneHeadingParagraphText(rawText)) {
        scriptRole = 'scene_heading';
      } else if (isScriptParentheticalParagraphText(rawText)) {
        scriptRole = 'parenthetical';
      }
    }
    const runs = rawRuns.map((run) => ({
      ...run,
      text: applyScriptRoleTextTransform(run.text, scriptRole),
    }));
    const text = textFromRuns(runs);
    let indentFirstLine: boolean;
    if (ctx.indentFlags !== null) {
      indentFirstLine = ctx.indentFlags[paragraphIndex] ?? true;
    } else {
      indentFirstLine =
        !ctx.inList &&
        scriptRole === 'body';
    }
    ctx.paragraphIndexRef.current += 1;
    lines.push({
      text: text || '',
      isHeading: false,
      indentFirstLine,
      scriptRole,
      runs,
    });
    if (
      text.trim()
      && (
        isScriptSceneHeadingRole(scriptRole)
        || isSceneHeadingParagraphText(rawText)
      )
    ) {
      const statsLine = takeNextExportScriptStatsLine(ctx.scriptStatsQueue);
      if (statsLine) {
        lines.push({
          text: statsLine,
          isHeading: false,
          indentFirstLine: false,
          isScriptStats: true,
          runs: [{ text: statsLine, isBold: false, isItalic: false }],
        });
      }
    }
  } else if (node.type === 'heading') {
    const level = node.attrs?.level || 1;
    const runs = extractTextRuns(node.content, footnoteMap);
    const text = textFromRuns(runs);
    lines.push({ text: text || '', isHeading: true, level, runs });
    const statsLine = takeNextExportScriptStatsLine(ctx.scriptStatsQueue);
    if (statsLine) {
      lines.push({
        text: statsLine,
        isHeading: false,
        indentFirstLine: false,
        isScriptStats: true,
        runs: [{ text: statsLine, isBold: false, isItalic: false }],
      });
    }
  } else if (node.type === 'bulletList' || node.type === 'orderedList') {
    if (node.content && Array.isArray(node.content)) {
      node.content.forEach((listItem: any, index: number) => {
        if (listItem.type === 'listItem' && listItem.content) {
          listItem.content.forEach((itemContent: any) => {
            const itemLines: Array<LineWithFootnotes> = [];
            convertTipTapToPDF(itemContent, itemLines, footnoteMap, { ...ctx, inList: true });
            const prefix = node.type === 'orderedList' ? `${index + 1}. ` : '• ';
            itemLines.forEach((line, idx) => {
              const prefixText = idx === 0 ? prefix : '  ';
              const prefixedRuns: TextRun[] = [{ text: prefixText, isBold: false, isItalic: false }, ...(line.runs || [])];
              lines.push({
                text: idx === 0 ? `${prefix}${line.text}` : `  ${line.text}`,
                isHeading: false,
                indentFirstLine: false,
                runs: prefixedRuns,
              });
            });
          });
        }
      });
    }
  } else if (node.content && Array.isArray(node.content)) {
    node.content.forEach((child: any) => {
      convertTipTapToPDF(child, lines, footnoteMap, ctx);
    });
  }
};

export const collectPdfExportLineTexts = (docJson: unknown): string[] => {
  const lines: Array<LineWithFootnotes> = [];
  convertTipTapToPDF(docJson, lines, new Map(), {
    indentFlags: null,
    scriptRoles: null,
    paragraphIndexRef: { current: 0 },
    inList: false,
    scriptStatsQueue: [],
  });
  return lines.map((line) => line.text);
};

export interface PdfHeadingNeighborContext {
  previousIsBlank: boolean;
  previousIsHeading: boolean;
  nextIsBlank: boolean;
  nextIsHeading: boolean;
  nextIsScriptStats: boolean;
}

export interface PdfHeadingGapSizesMm {
  afterBody: number;
  afterHeading: number;
  betweenHeadings: number;
}

export const shouldAddPdfHeadingTopGap = (ctx: PdfHeadingNeighborContext): boolean => {
  if (ctx.previousIsBlank || ctx.previousIsHeading) {
    return false;
  }
  return true;
};

export const pdfHeadingGapAfterMm = (
  ctx: PdfHeadingNeighborContext,
  gaps: PdfHeadingGapSizesMm,
): number => {
  if (ctx.nextIsScriptStats || ctx.nextIsBlank) {
    return 0;
  }
  if (ctx.nextIsHeading) {
    return gaps.betweenHeadings;
  }
  return gaps.afterHeading;
};

export const exportToPDF = async (options: ExportOptions): Promise<void> => {
  const { editor, ydoc, documentTitle, projectTitle, documentId, token, apiBase, exportComments, exportSuggestions, includeCoverPage, coverAuthorName, comments, suggestions, t } = options;

  if (!editor) return;

  try {
    const defaultExportLayoutOptions: ExportLayoutOptions = {
      pageNumberPosition: 'bottom_right',
      lineSpacing: 1.5,
      marginsCm: {
        top: 2.5,
        right: 2.5,
        bottom: 2.5,
        left: 2.5,
      },
    };
    const exportLayoutOptions = options.exportLayoutOptions ?? options.wordExportOptions ?? defaultExportLayoutOptions;
    const pdfFontFamily = getJsPdfFontFamily(exportLayoutOptions.exportFontId);
    const bodyFontSizePt = getExportBodyFontSizePt(exportLayoutOptions.exportFontSizePt);
    const lineSpacingMultiplier = typeof exportLayoutOptions.lineSpacing === 'number' ? exportLayoutOptions.lineSpacing : 1.5;
    const cmToMm = (cm: number): number => cm * 10;
    const marginsMm = {
      top: cmToMm(exportLayoutOptions.marginsCm.top),
      right: cmToMm(exportLayoutOptions.marginsCm.right),
      bottom: cmToMm(exportLayoutOptions.marginsCm.bottom),
      left: cmToMm(exportLayoutOptions.marginsCm.left),
    };

    const json = resolveExportDocumentJson(editor, ydoc, ydocToJSON);
    if (!json) return;
    
    const { footnotes, footnoteMap } = extractFootnotes(json);
    const lines: Array<LineWithFootnotes> = [];
    const expectedParagraphCount = countParagraphsInJsonMain(json);
    const exportLayouts = getParagraphExportLayouts(editor.state.doc);
    let indentFlags: boolean[] | null = exportLayouts.map((layout) => layout.indentFirstLine);
    let scriptRoles: ExportScriptParagraphRole[] | null = exportLayouts.map((layout) => layout.role);
    if (exportLayouts.length !== expectedParagraphCount) {
      indentFlags = null;
      scriptRoles = null;
    }
    const paragraphIndexRef = { current: 0 };
    const scriptStatsQueue = collectExportScriptStatsQueue(editor.state.doc, t);
    convertTipTapToPDF(json, lines, footnoteMap, {
      indentFlags,
      scriptRoles,
      paragraphIndexRef,
      inList: false,
      scriptStatsQueue,
    });

    const appendix = resolveExportAppendixContent({
      exportComments,
      exportSuggestions,
      comments,
      suggestions,
    });
    const commentMarkers = buildPdfCommentMarkers(appendix.comments);
    const suggestionMarkers = buildPdfSuggestionMarkers(appendix.suggestions);
    const annotatedLines = annotatePdfLinesWithMarkers(lines, [
      ...commentMarkers,
      ...suggestionMarkers,
    ]);

    const pdf = new jsPDF();
    await ensurePdfExportFonts(pdf, pdfFontFamily);

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const footerHeight = footnotes.length > 0 ? 40 : 0;
    const contentBottom = pageHeight - marginsMm.bottom - footerHeight;
    const maxWidth = pageWidth - marginsMm.left - marginsMm.right;
    const title = documentTitle || 'Proyecto';

    if (includeCoverPage) {
      const coverMaxWidth = maxWidth;
      const authorOnCover = coverAuthorName && coverAuthorName.trim().length > 0 ? coverAuthorName.trim() : undefined;

      pdf.setFontSize(26);
      pdf.setFont(pdfFontFamily, 'bold');
      pdf.setLineHeightFactor(lineSpacingMultiplier);
      const coverLines = pdf.splitTextToSize(title, coverMaxWidth);
      const titleLineAdvance = pdf.getLineHeight() / pdf.internal.scaleFactor;
      const titleBlockHeight = coverLines.length * titleLineAdvance;

      let authorLines: string[] = [];
      let authorLineAdvance = 0;
      let authorBlockHeight = 0;
      const gapBetweenTitleAndAuthorMm = 5;
      if (authorOnCover) {
        pdf.setFontSize(14);
        pdf.setFont(pdfFontFamily, 'normal');
        pdf.setLineHeightFactor(lineSpacingMultiplier);
        authorLines = pdf.splitTextToSize(authorOnCover, coverMaxWidth);
        authorLineAdvance = pdf.getLineHeight() / pdf.internal.scaleFactor;
        authorBlockHeight = authorLines.length * authorLineAdvance + gapBetweenTitleAndAuthorMm;
      }

      const totalCoverBlockHeight = titleBlockHeight + authorBlockHeight;
      let coverY = pageHeight / 2 - totalCoverBlockHeight / 2 + titleLineAdvance * 0.35;

      pdf.setFontSize(26);
      pdf.setFont(pdfFontFamily, 'bold');
      pdf.setLineHeightFactor(lineSpacingMultiplier);
      coverLines.forEach((ln: string) => {
        pdf.text(ln, pageWidth / 2, coverY, { align: 'center', maxWidth: coverMaxWidth });
        coverY += titleLineAdvance;
      });

      if (authorOnCover && authorLines.length > 0) {
        coverY += gapBetweenTitleAndAuthorMm;
        pdf.setFontSize(14);
        pdf.setFont(pdfFontFamily, 'normal');
        pdf.setLineHeightFactor(lineSpacingMultiplier);
        authorLines.forEach((ln: string) => {
          pdf.text(ln, pageWidth / 2, coverY, { align: 'center', maxWidth: coverMaxWidth });
          coverY += authorLineAdvance;
        });
      }

      pdf.addPage();
    }

    let yPosition = marginsMm.top;
    const footnotesPerPage = new Map<number, Set<number>>();
    let currentPage = includeCoverPage ? 2 : 1;

    if (!includeCoverPage) {
      pdf.setFontSize(18);
      pdf.setFont(pdfFontFamily, 'bold');
      pdf.text(title, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
      yPosition += 10;
    }

    pdf.setFontSize(bodyFontSizePt);
    pdf.setFont(pdfFontFamily, 'normal');
    pdf.setLineHeightFactor(lineSpacingMultiplier);

    const getBaseLineHeight = (): number => pdf.getTextDimensions('Mg').h;
    const getLineAdvance = (): number => pdf.getLineHeight() / pdf.internal.scaleFactor;
    const applyBodyTextMetrics = (): void => {
      pdf.setFontSize(bodyFontSizePt);
      pdf.setFont(pdfFontFamily, 'normal');
      pdf.setLineHeightFactor(lineSpacingMultiplier);
    };
    const getBodyLineAdvance = (): number => {
      applyBodyTextMetrics();
      return getLineAdvance();
    };
    const headingGapSizesMm = (): PdfHeadingGapSizesMm => {
      const bodyLine = getBodyLineAdvance();
      return {
        afterBody: Math.max(4, bodyLine * 0.9),
        afterHeading: Math.max(1.5, bodyLine * 0.35),
        betweenHeadings: Math.max(3, bodyLine * 0.7),
      };
    };

    const registerFootnotesInText = (text: string): void => {
      const footnotePattern = /\[(\d+)\]/g;
      let match: RegExpExecArray | null;
      while ((match = footnotePattern.exec(text)) !== null) {
        const footnoteNumber = Number.parseInt(match[1], 10);
        if (!Number.isFinite(footnoteNumber)) continue;
        if (!footnotesPerPage.has(currentPage)) {
          footnotesPerPage.set(currentPage, new Set<number>());
        }
        footnotesPerPage.get(currentPage)!.add(footnoteNumber);
      }
    };

    const getTextBlockHeight = (wrappedText: string[]): number => {
      const anyPdf = pdf as unknown as { getTextDimensions?: (text: unknown) => { h: number } };
      const dimensionsFromLines = anyPdf.getTextDimensions?.(wrappedText);
      if (dimensionsFromLines && typeof dimensionsFromLines.h === 'number' && dimensionsFromLines.h > 0) {
        return dimensionsFromLines.h;
      }

      if (wrappedText.length <= 1) {
        return getBaseLineHeight();
      }

      return getBaseLineHeight() + (wrappedText.length - 1) * getLineAdvance();
    };

    const addContentPage = (): void => {
      renderPageFootnotes();
      pdf.addPage();
      currentPage++;
      yPosition = marginsMm.top;
    };

    const drawJustifiedLine = (line: string, x: number, y: number, width: number): void => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      const words = trimmedLine.split(/\s+/).filter(Boolean);
      if (words.length <= 1) {
        pdf.text(trimmedLine, x, y);
        return;
      }

      const currentLineWidth = pdf.getTextWidth(trimmedLine);
      if (currentLineWidth >= width) {
        pdf.text(trimmedLine, x, y);
        return;
      }

      const spaceWidth = pdf.getTextWidth(' ');
      const gaps = words.length - 1;
      const extraSpaceTotal = width - currentLineWidth;
      const extraSpacePerGap = extraSpaceTotal / gaps;

      let currentX = x;
      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        pdf.text(word, currentX, y);
        currentX += pdf.getTextWidth(word);
        if (i < gaps) {
          currentX += spaceWidth + extraSpacePerGap;
        }
      }
    };

    const drawStyledWordsLine = (
      words: StyledWord[],
      x: number,
      y: number,
      width: number,
      justify: boolean,
    ): void => {
      if (words.length === 0) {
        return;
      }
      if (words.length === 1) {
        const onlyWord = words[0];
        pdf.setFont(pdfFontFamily, getPdfFontStyle(onlyWord.isBold, onlyWord.isItalic));
        pdf.text(onlyWord.text, x, y);
        return;
      }
      const baseSpaceWidth = pdf.getTextWidth(' ');
      let wordsWidth = 0;
      words.forEach((word) => {
        pdf.setFont(pdfFontFamily, getPdfFontStyle(word.isBold, word.isItalic));
        wordsWidth += pdf.getTextWidth(word.text);
      });
      const gaps = words.length - 1;
      const extraSpacePerGap = justify ? Math.max(0, (width - wordsWidth - baseSpaceWidth * gaps) / gaps) : 0;
      let currentX = x;
      words.forEach((word, index) => {
        pdf.setFont(pdfFontFamily, getPdfFontStyle(word.isBold, word.isItalic));
        pdf.text(word.text, currentX, y);
        currentX += pdf.getTextWidth(word.text);
        if (index < gaps) {
          currentX += baseSpaceWidth + extraSpacePerGap;
        }
      });
    };

    const renderWrappedLinesAcrossPages = (input: {
      wrappedLines: string[];
      x: number;
      maxWidth: number;
      align: 'left' | 'justify';
      fontSize: number;
      isBold: boolean;
      lineHeightFactor?: number;
    }): void => {
      const { wrappedLines, x, maxWidth, align, fontSize, isBold } = input;
      const lineHeightFactor = input.lineHeightFactor ?? lineSpacingMultiplier;
      const applyFont = (): void => {
        pdf.setFontSize(fontSize);
        pdf.setFont(pdfFontFamily, isBold ? 'bold' : 'normal');
        pdf.setLineHeightFactor(lineHeightFactor);
      };

      applyFont();

      for (let i = 0; i < wrappedLines.length; i++) {
        const wrappedLine = wrappedLines[i];
        const lineAdvance = getLineAdvance();
        if (yPosition + lineAdvance > contentBottom) {
          addContentPage();
          applyFont();
        }

        registerFootnotesInText(wrappedLine);
        if (align === 'justify' && i < wrappedLines.length - 1) {
          drawJustifiedLine(wrappedLine, x, yPosition, maxWidth);
        } else {
          pdf.text(wrappedLine, x, yPosition, { align: 'left', maxWidth });
        }
        yPosition += lineAdvance;
      }
    };

    const renderBodyParagraphWithFirstLineIndent = (
      line: LineWithFootnotes,
      options?: { omitTrailingSpacing?: boolean },
    ): void => {
      const bodyFontSize = bodyFontSizePt;
      const scriptRole = line.scriptRole ?? 'body';
      const isCharacterCue = isScriptCharacterCueRole(scriptRole);
      const isSceneHeading = isScriptSceneHeadingRole(scriptRole);
      const isParenthetical = isScriptParentheticalRole(scriptRole);
      const isDialogue = isScriptDialogueRole(scriptRole);
      const dialogueInsetMm = isDialogue ? maxWidth * SCRIPT_DIALOGUE_INSET_RATIO : 0;
      const paragraphMaxWidth = Math.max(8, maxWidth - dialogueInsetMm * 2);
      const paragraphOriginX = marginsMm.left + dialogueInsetMm;
      const useIndent =
        !isCharacterCue
        && !isParenthetical
        && !isDialogue
        && !isSceneHeading
        && line.indentFirstLine !== false;
      const indentMm = useIndent ? firstLineIndentMmFromBodyFontSizePt(bodyFontSize) : 0;
      if (isSceneHeading) {
        const sceneTopGapMm = scriptSceneHeadingGapMm(bodyFontSize, getLineAdvance());
        if (yPosition + sceneTopGapMm + getLineAdvance() > contentBottom) {
          addContentPage();
          pdf.setFontSize(bodyFontSize);
          pdf.setFont(pdfFontFamily, 'normal');
          pdf.setLineHeightFactor(lineSpacingMultiplier);
        }
        yPosition += sceneTopGapMm;
      }
      if (isCharacterCue) {
        const cueTopGapMm = scriptCharacterCueTopGapMm(bodyFontSize);
        if (yPosition + cueTopGapMm + getLineAdvance() > contentBottom) {
          addContentPage();
          pdf.setFontSize(bodyFontSize);
          pdf.setFont(pdfFontFamily, 'normal');
          pdf.setLineHeightFactor(lineSpacingMultiplier);
        }
        yPosition += cueTopGapMm;
      }
      const segments = line.text.split('\n');
      const runSegments = splitRunsByLineBreak(line.runs || [{ text: line.text, isBold: false, isItalic: false }]);
      segments.forEach((segment, segIdx) => {
        if (segment.trim() === '' && segments.length > 1) {
          yPosition += getLineAdvance();
          return;
        }
        const styledWords = getStyledWordsFromRuns(runSegments[segIdx] || []);
        let wordCursor = 0;
        const wrapIndentMm = useIndent && segIdx === 0 ? indentMm : 0;
        const wrappedLines = wrapParagraphLinesWithOptionalFirstLineIndent(
          pdf,
          segment,
          paragraphMaxWidth,
          wrapIndentMm,
        );
        for (let i = 0; i < wrappedLines.length; i++) {
          const lineStr = wrappedLines[i];
          const lineAdvance = getLineAdvance();
          if (yPosition + lineAdvance > contentBottom) {
            addContentPage();
            pdf.setFontSize(bodyFontSize);
            pdf.setFont(pdfFontFamily, 'normal');
            pdf.setLineHeightFactor(lineSpacingMultiplier);
          }
          registerFootnotesInText(lineStr);
          const firstPhysicalLine = wrapIndentMm > 0 && i === 0;
          const x = firstPhysicalLine ? paragraphOriginX + wrapIndentMm : paragraphOriginX;
          const lineBoxWidth = firstPhysicalLine ? paragraphMaxWidth - wrapIndentMm : paragraphMaxWidth;
          const justifyLine =
            !isCharacterCue &&
            !isParenthetical &&
            !isDialogue &&
            i < wrappedLines.length - 1 &&
            lineStr.trim().length > 0;
          const lineWordsText = lineStr.trim().match(/\S+/g) || [];
          const lineWords = lineWordsText.map((text) => {
            const style = styledWords[wordCursor];
            if (style) {
              wordCursor += 1;
              return { text, isBold: style.isBold, isItalic: style.isItalic };
            }
            return { text, isBold: false, isItalic: false };
          });
          if (isCharacterCue || isParenthetical) {
            pdf.setFont(pdfFontFamily, 'normal');
            pdf.text(lineStr.trim(), pageWidth / 2, yPosition, {
              align: 'center',
              maxWidth: paragraphMaxWidth,
            });
          } else if (lineWords.length === 0) {
            pdf.setFont(pdfFontFamily, 'normal');
            pdf.text(lineStr, x, yPosition, { align: 'left', maxWidth: lineBoxWidth });
          } else if (justifyLine) {
            drawStyledWordsLine(lineWords, x, yPosition, lineBoxWidth, true);
          } else if (lineWords.length !== lineWordsText.length) {
            drawJustifiedLine(lineStr, x, yPosition, lineBoxWidth);
          } else {
            drawStyledWordsLine(lineWords, x, yPosition, lineBoxWidth, false);
          }
          yPosition += lineAdvance;
        }
      });
      if (options?.omitTrailingSpacing) {
        return;
      }
      if (isSceneHeading) {
        yPosition += scriptSceneHeadingGapMm(bodyFontSize, getLineAdvance());
      }
    };

    const renderPageFootnotes = (): void => {
      const pageFootnotes = footnotesPerPage.get(currentPage);
      if (!pageFootnotes || pageFootnotes.size === 0) return;

      const sortedFootnotes = Array.from(pageFootnotes).sort((a, b) => a - b);
      const pageNumberArea = 15;
      const footnoteBottom = pageHeight - marginsMm.bottom - pageNumberArea;
      let footnoteY = pageHeight - marginsMm.bottom - footerHeight + 5;
      
      pdf.setFontSize(9);
      pdf.setFont(pdfFontFamily, 'normal');
        pdf.setLineHeightFactor(1.1);
      
      const footnoteSeparatorY = footnoteY - 3;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(marginsMm.left, footnoteSeparatorY, pageWidth - marginsMm.right, footnoteSeparatorY);
      
      sortedFootnotes.forEach((footnoteNumber) => {
        const footnote = footnotes.find(fn => footnoteMap.get(fn.id) === footnoteNumber);
        if (!footnote) return;

        if (footnoteY > footnoteBottom) {
          return;
        }

        pdf.setFont(pdfFontFamily, 'bold');
        const footnoteLabel = `${footnoteNumber}. `;
        const labelWidth = pdf.getTextWidth(footnoteLabel);
        pdf.text(footnoteLabel, marginsMm.left, footnoteY, {});
        
        pdf.setFont(pdfFontFamily, 'normal');
        const footnoteContentWidth = maxWidth - labelWidth - 5;
        
        footnote.content.forEach((fnLine) => {
          if (footnoteY > footnoteBottom) {
            return;
          }
          const splitText = pdf.splitTextToSize(fnLine.text, footnoteContentWidth);
          const blockHeight = getTextBlockHeight(splitText);
          if (footnoteY + blockHeight > footnoteBottom) {
            return;
          }
          pdf.text(splitText, marginsMm.left + labelWidth + 5, footnoteY, { align: 'justify', maxWidth: footnoteContentWidth });
          footnoteY += blockHeight;
        });

        footnoteY += 2;
      });
    };

    annotatedLines.forEach((line, index) => {
      if (line.text.trim() === '') {
        const lineAdvance = getBodyLineAdvance();
        if (yPosition + lineAdvance > contentBottom) {
          addContentPage();
          applyBodyTextMetrics();
        }
        yPosition += lineAdvance;
        return;
      }

      const previousLine = annotatedLines[index - 1];
      const nextLine = annotatedLines[index + 1];
      const nextIsScriptStats = Boolean(nextLine?.isScriptStats);

      if (line.isHeading) {
        const headingNeighbors: PdfHeadingNeighborContext = {
          previousIsBlank: Boolean(previousLine && previousLine.text.trim() === ''),
          previousIsHeading: Boolean(previousLine?.isHeading),
          nextIsBlank: Boolean(nextLine && nextLine.text.trim() === ''),
          nextIsHeading: Boolean(nextLine?.isHeading),
          nextIsScriptStats,
        };
        const gaps = headingGapSizesMm();
        if (shouldAddPdfHeadingTopGap(headingNeighbors)) {
          yPosition += gaps.afterBody;
        }
        const fontSize = getPdfHeadingFontSizePt(line.level, bodyFontSizePt);
        pdf.setFontSize(fontSize);
        pdf.setFont(pdfFontFamily, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.setLineHeightFactor(1);
        const splitText = pdf.splitTextToSize(line.text, maxWidth);
        renderWrappedLinesAcrossPages({
          wrappedLines: splitText,
          x: marginsMm.left,
          maxWidth,
          align: 'left',
          fontSize,
          isBold: true,
          lineHeightFactor: 1,
        });
        yPosition += pdfHeadingGapAfterMm(headingNeighbors, gaps);
      } else if (line.isScriptStats) {
        const fontSize = EXPORT_SCRIPT_STATS_FONT_SIZE_PT;
        pdf.setFontSize(fontSize);
        pdf.setFont(pdfFontFamily, 'normal');
        pdf.setTextColor(...EXPORT_SCRIPT_STATS_PDF_GREY_RGB);
        pdf.setLineHeightFactor(1);
        const splitText = pdf.splitTextToSize(line.text, maxWidth);
        renderWrappedLinesAcrossPages({
          wrappedLines: splitText,
          x: marginsMm.left,
          maxWidth,
          align: 'left',
          fontSize,
          isBold: false,
          lineHeightFactor: 1,
        });
        pdf.setTextColor(0, 0, 0);
        if (previousLine && isScriptSceneHeadingRole(previousLine.scriptRole ?? 'body')) {
          yPosition += scriptSceneHeadingGapMm(bodyFontSizePt, getBodyLineAdvance());
        } else {
          yPosition += pdfHeadingGapAfterMm(
            {
              previousIsBlank: false,
              previousIsHeading: false,
              nextIsBlank: Boolean(nextLine && nextLine.text.trim() === ''),
              nextIsHeading: Boolean(nextLine?.isHeading),
              nextIsScriptStats: false,
            },
            headingGapSizesMm(),
          );
        }
      } else {
        pdf.setFontSize(bodyFontSizePt);
        pdf.setFont(pdfFontFamily, 'normal');
        pdf.setTextColor(0, 0, 0);
        pdf.setLineHeightFactor(lineSpacingMultiplier);
        const isSceneHeading = isScriptSceneHeadingRole(line.scriptRole ?? 'body');
        renderBodyParagraphWithFirstLineIndent(line, {
          omitTrailingSpacing: nextIsScriptStats && isSceneHeading,
        });
      }
    });

    if (appendix.comments.length > 0) {
      if (yPosition > contentBottom - 20) {
        renderPageFootnotes();
        pdf.addPage();
        currentPage++;
        yPosition = marginsMm.top;
      } else {
        yPosition += 10;
      }

      pdf.setFontSize(getPdfHeadingFontSizePt(1, bodyFontSizePt));
      pdf.setFont(pdfFontFamily, 'bold');
      const commentsTitle = t('comments');
      pdf.text(commentsTitle, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
      yPosition += 10;

      pdf.setFontSize(bodyFontSizePt);
      pdf.setFont(pdfFontFamily, 'normal');

      appendix.comments.forEach((comment, index) => {
        if (yPosition > contentBottom - 30) {
          renderPageFootnotes();
          pdf.addPage();
          currentPage++;
          yPosition = marginsMm.top;
        } else {
          yPosition += 5;
        }

        pdf.setFont(pdfFontFamily, 'bold');
        const commentNumber = `${commentMarkers[index]?.label ?? `[c${index + 1}]`} `;
        pdf.text(commentNumber, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
        yPosition += 6;

        pdf.setFont(pdfFontFamily, 'normal');
        const selectedTextLabel = `${t('selected_text')}: `;
        const selectedTextValue = comment.selectedText || '';
        const selectedTextFull = selectedTextLabel + selectedTextValue;
        const selectedTextSplit = pdf.splitTextToSize(selectedTextFull, maxWidth);
        const selectedHeight = selectedTextSplit.length * 6;
        if (yPosition + selectedHeight > contentBottom) {
          renderPageFootnotes();
          pdf.addPage();
          currentPage++;
          yPosition = marginsMm.top;
        }
        pdf.text(selectedTextFull, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
        yPosition += selectedHeight;

        const commentLabel = `${t('comment')}: `;
        const commentValue = comment.content;
        const commentFull = commentLabel + commentValue;
        const commentSplit = pdf.splitTextToSize(commentFull, maxWidth);
        const commentHeight = commentSplit.length * 6;
        if (yPosition + commentHeight > contentBottom) {
          renderPageFootnotes();
          pdf.addPage();
          currentPage++;
          yPosition = marginsMm.top;
        }
        pdf.text(commentFull, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
        yPosition += commentHeight;

        if (comment.userEmail) {
          pdf.setFont(pdfFontFamily, 'italic');
          const userText = `${t('user')}: ${comment.userEmail}`;
          const userSplit = pdf.splitTextToSize(userText, maxWidth);
          const userHeight = userSplit.length * 6;
          if (yPosition + userHeight > contentBottom) {
            renderPageFootnotes();
            pdf.addPage();
            currentPage++;
            yPosition = marginsMm.top;
          }
          pdf.text(userText, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
          yPosition += userHeight;
          pdf.setFont(pdfFontFamily, 'normal');
        }

        const replies = comment.replies ?? [];
        replies.forEach((reply) => {
          if (yPosition > contentBottom - 20) {
            renderPageFootnotes();
            pdf.addPage();
            currentPage++;
            yPosition = marginsMm.top;
          } else {
            yPosition += 4;
          }

          pdf.setFont(pdfFontFamily, 'italic');
          const replyAuthor = reply.userEmail || t('user');
          const replyHeader = `${t('reply')} · ${replyAuthor}`;
          const replyHeaderSplit = pdf.splitTextToSize(replyHeader, maxWidth - 5);
          const replyHeaderHeight = replyHeaderSplit.length * 6;
          if (yPosition + replyHeaderHeight > contentBottom) {
            renderPageFootnotes();
            pdf.addPage();
            currentPage++;
            yPosition = marginsMm.top;
          }
          pdf.text(replyHeader, marginsMm.left + 5, yPosition, { align: 'justify', maxWidth: maxWidth - 5 });
          yPosition += replyHeaderHeight;

          pdf.setFont(pdfFontFamily, 'normal');
          const replyBodySplit = pdf.splitTextToSize(reply.comment, maxWidth - 5);
          const replyBodyHeight = replyBodySplit.length * 6;
          if (yPosition + replyBodyHeight > contentBottom) {
            renderPageFootnotes();
            pdf.addPage();
            currentPage++;
            yPosition = marginsMm.top;
          }
          pdf.text(reply.comment, marginsMm.left + 5, yPosition, { align: 'justify', maxWidth: maxWidth - 5 });
          yPosition += replyBodyHeight;
        });

        yPosition += 3;
      });
    }
    if (appendix.suggestions.length > 0) {
      if (yPosition > contentBottom - 20) {
        renderPageFootnotes();
        pdf.addPage();
        currentPage++;
        yPosition = marginsMm.top;
      } else {
        yPosition += 10;
      }

      pdf.setFontSize(getPdfHeadingFontSizePt(1, bodyFontSizePt));
      pdf.setFont(pdfFontFamily, 'bold');
      const suggestionsTitle = t('suggestions');
      pdf.text(suggestionsTitle, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
      yPosition += 10;

      pdf.setFontSize(bodyFontSizePt);
      pdf.setFont(pdfFontFamily, 'normal');

      appendix.suggestions.forEach((suggestion, index) => {
        if (yPosition > contentBottom - 30) {
          renderPageFootnotes();
          pdf.addPage();
          currentPage++;
          yPosition = marginsMm.top;
        } else {
          yPosition += 5;
        }

        pdf.setFont(pdfFontFamily, 'bold');
        const suggestionNumber = `${suggestionMarkers[index]?.label ?? `[s${index + 1}]`} `;
        pdf.text(suggestionNumber, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
        yPosition += 6;

        pdf.setFont(pdfFontFamily, 'normal');
        const originalTextLabel = `${t('original_text')}: `;
        const originalTextValue = suggestion.originalText;
        const originalTextFull = originalTextLabel + originalTextValue;
        const originalTextSplit = pdf.splitTextToSize(originalTextFull, maxWidth);
        const originalHeight = originalTextSplit.length * 6;
        if (yPosition + originalHeight > contentBottom) {
          renderPageFootnotes();
          pdf.addPage();
          currentPage++;
          yPosition = marginsMm.top;
        }
        pdf.text(originalTextFull, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
        yPosition += originalHeight;

        const suggestionLabel = `${t('suggestion')}: `;
        const suggestionValue = suggestion.suggestionText;
        const suggestionFull = suggestionLabel + suggestionValue;
        const suggestionSplit = pdf.splitTextToSize(suggestionFull, maxWidth);
        const suggestionHeight = suggestionSplit.length * 6;
        if (yPosition + suggestionHeight > contentBottom) {
          renderPageFootnotes();
          pdf.addPage();
          currentPage++;
          yPosition = marginsMm.top;
        }
        pdf.text(suggestionFull, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
        yPosition += suggestionHeight;

        const typeLabel = `${t('type')}: `;
        const typeValue = suggestion.issueType === 'orthographic' ? t('orthographic') : suggestion.issueType === 'strength' ? t('strength') : t('other');
        const typeFull = typeLabel + typeValue;
        const typeSplit = pdf.splitTextToSize(typeFull, maxWidth);
        const typeHeight = typeSplit.length * 6;
        if (yPosition + typeHeight > contentBottom) {
          renderPageFootnotes();
          pdf.addPage();
          currentPage++;
          yPosition = marginsMm.top;
        }
        pdf.text(typeFull, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
        yPosition += typeHeight;

        const feedbackLabel = `${t('suggestion_label')}: `;
        const feedbackValue = suggestion.feedbackText;
        const feedbackFull = feedbackLabel + feedbackValue;
        const feedbackSplit = pdf.splitTextToSize(feedbackFull, maxWidth);
        const feedbackHeight = feedbackSplit.length * 6;
        if (yPosition + feedbackHeight > contentBottom) {
          renderPageFootnotes();
          pdf.addPage();
          currentPage++;
          yPosition = marginsMm.top;
        }
        pdf.text(feedbackFull, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
        yPosition += feedbackHeight;

        pdf.setFont(pdfFontFamily, 'italic');
        const userText = `${t('user')}: ${t('automatic_analysis')}`;
        const userSplit = pdf.splitTextToSize(userText, maxWidth);
        const userHeight = userSplit.length * 6;
        if (yPosition + userHeight > contentBottom) {
          renderPageFootnotes();
          pdf.addPage();
          currentPage++;
          yPosition = marginsMm.top;
        }
        pdf.text(userText, marginsMm.left, yPosition, { align: 'justify', maxWidth: maxWidth });
        yPosition += userHeight;
        pdf.setFont(pdfFontFamily, 'normal');

        yPosition += 3;
      });
    }

    renderPageFootnotes();

    if (exportLayoutOptions.pageNumberPosition !== 'none') {
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setFont(pdfFontFamily, 'normal');
        const pageNumberText = `${i} / ${totalPages}`;
        const textWidth = pdf.getTextWidth(pageNumberText);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const xPosition =
          exportLayoutOptions.pageNumberPosition === 'bottom_left'
            ? marginsMm.left
            : exportLayoutOptions.pageNumberPosition === 'bottom_middle'
              ? (pageWidth - textWidth) / 2
              : pageWidth - marginsMm.right - textWidth;
        const yPosition = pdf.internal.pageSize.getHeight() - 10;
        pdf.text(pageNumberText, xPosition, yPosition);
      }
    }

    pdf.save(getExportFilename(documentTitle, projectTitle, 'pdf'));

    if (token) {
      try {
        await fetch(`${apiBase}/api/exports`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            documentId: documentId || null,
            exportType: 'pdf',
            documentTitle: documentTitle || null,
          }),
        });
      } catch (err) {
        console.error('Error tracking export:', err);
      }
    }
  } catch (err) {
    console.error('Error exporting to PDF:', err);
    alert('Error al exportar el proyecto. Por favor, inténtalo de nuevo.');
  }
};

